/**
 * Article Detail Wrapper with 301 Redirect Handler
 * Checks for old slugs and redirects to current slug if needed
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import ArticleDetail from "@/pages/ArticleDetail";
import { DeclinedCard404DE } from "@/components/DeclinedCard404DE";

export default function ArticleDetailWrapper() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    checkForRedirect();
  }, [slug]);

  const checkForRedirect = async () => {
    if (!slug) {
      setChecking(false);
      return;
    }

    try {
      // Special embedded articles that should NOT be accessible via direct URL
      // These are embedded in specific pages and should only appear there
      const EMBEDDED_ARTICLE_SLUGS = [
        'forside',                          // Embedded on homepage (/)
        'alle-kredittkort-i-norge-listing'  // Embedded on card listing (/kort)
      ];

      // Block direct access to embedded articles
      if (EMBEDDED_ARTICLE_SLUGS.includes(slug)) {
        setNotFound(true);
        setChecking(false);
        return;
      }

      // First check if this slug exists as a current article
      const { data: currentArticle } = await supabase
        .from('articles')
        .select('slug')
        .eq('slug', slug)
        .eq('status', 'published')
        .eq('show_on_homepage', false)
        .maybeSingle();

      if (currentArticle) {
        // Article exists with this slug, render normally
        setChecking(false);
        return;
      }

      // Check if this is an old slug that needs redirecting
      const { data: redirect } = await supabase
        .from('article_slug_redirects')
        .select('article_id')
        .eq('old_slug', slug)
        .maybeSingle();

      if (redirect) {
        // Get the current slug for this article
        const { data: article } = await supabase
          .from('articles')
          .select('slug')
          .eq('id', redirect.article_id)
          .eq('status', 'published')
          .eq('show_on_homepage', false)
          .single();

        if (article) {
          // Perform 301 redirect
          navigate(`/${article.slug}`, { replace: true });
          return;
        }
      }

      // No redirect found, show 404
      setNotFound(true);
      setChecking(false);
    } catch (error) {
      console.error('Error checking for redirect:', error);
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-background/95">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="space-y-6 animate-pulse">
            {/* Breadcrumb skeleton */}
            <div className="h-4 w-48 bg-muted rounded-sm" />
            
            {/* Article header skeleton */}
            <div className="space-y-4">
              <div className="h-8 w-3/4 bg-muted rounded-sm" />
              <div className="flex gap-4">
                <div className="h-4 w-24 bg-muted rounded-sm" />
                <div className="h-4 w-32 bg-muted rounded-sm" />
              </div>
            </div>
            
            {/* Featured image skeleton */}
            <div className="w-full aspect-video bg-muted rounded-lg" />
            
            {/* Content skeleton */}
            <div className="space-y-3">
              <div className="h-4 w-full bg-muted rounded-sm" />
              <div className="h-4 w-full bg-muted rounded-sm" />
              <div className="h-4 w-5/6 bg-muted rounded-sm" />
              <div className="h-4 w-full bg-muted rounded-sm" />
              <div className="h-4 w-4/5 bg-muted rounded-sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return <DeclinedCard404DE onNavigateHome={() => navigate('/')} />;
  }

  return <ArticleDetail />;
}
